import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useParams, Link } from 'react-router-dom';
import { API_BASE } from '../../config';
import { LLMModelSelector } from './LLMModelSelector';
import { useLocale } from '../../contexts/LocaleContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { SuggestionDetailAdminModal } from './SuggestionDetailAdminModal';
import { StyleNotesCalibrationPanel } from './StyleNotesCalibrationPanel';
import { AdminAccessDenied } from './AdminAccessDenied';
import {
  ADMIN_BADGE_DANGER_CLASSNAME,
  ADMIN_BADGE_INFO_CLASSNAME,
  ADMIN_BADGE_NEUTRAL_CLASSNAME,
  ADMIN_BADGE_SUCCESS_CLASSNAME,
  ADMIN_BADGE_WARNING_CLASSNAME,
  ADMIN_BODY_TEXT_CLASSNAME,
  ADMIN_DIVIDER_CLASSNAME,
  ADMIN_EMPTY_BOX_CLASSNAME,
  ADMIN_FIELD_LABEL_CLASSNAME,
  ADMIN_MODAL_CLASSNAME,
  ADMIN_MODAL_OVERLAY_CLASSNAME,
  ADMIN_MUTED_TEXT_CLASSNAME,
  ADMIN_PAGE_CLASSNAME,
  ADMIN_PANEL_CLASSNAME,
  ADMIN_PANEL_TITLE_CLASSNAME,
  ADMIN_SECTION_TITLE_CLASSNAME,
  ADMIN_STAT_CARD_CLASSNAME,
  ADMIN_STAT_LABEL_CLASSNAME,
  ADMIN_STAT_VALUE_CLASSNAME,
  ADMIN_STATS_ROW_CLASSNAME,
  ADMIN_TABLE_CELL_MUTED_CLASSNAME,
  ADMIN_TABLE_CLASSNAME,
  ADMIN_TABLE_LINK_CLASSNAME,
  AdminLoadingState,
  AdminPaginationBar,
  AdminTableWrap,
} from './admin-ui';
import { UserCreditsActivityChart } from './UserCreditsActivityChart';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import { CancelButton } from '../ui/cancel-button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { cn } from '../../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

interface User {
  id: string;
  email: string;
  admin: boolean;
  disabled: boolean;
  tier: 'free' | 'pro' | 'editorial';
  maxProjects: number | null;
  maxDocumentsPerProject: number | null;
  maxResourcesPerProject: number | null;
  storageQuotaMb: number | null;
  fixedLlmModel: string | null;
  invitationCodeUsed: string | null;
  internalNote: string | null;
  preferredLanguageCode: string;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  subscriptionStatus: 'active' | 'past_due' | 'canceled' | 'unpaid' | 'trialing' | 'incomplete' | 'incomplete_expired' | 'paused' | null;
  subscriptionCurrentPeriodStart: string | null;
  subscriptionCurrentPeriodEnd: string | null;
  subscriptionCancelAtPeriodEnd: boolean;
  billingCurrency: 'eur' | 'usd' | null;
  billingInterval: 'month' | 'year' | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface Document {
  id: string;
  title: string;
  projectId: string | null;
  createdAt: string;
  updatedAt: string;
  lastModified: string;
  trashed: boolean;
  styleNotes: string | null;
}

interface Project {
  id: string;
  title: string;
  documentCount: number;
  resourceCount: number;
}

interface LLMCall {
  id: string;
  userId: string | null;
  documentId: string | null;
  triggerSource: string;
  provider: 'gemini' | 'openai';
  model: string;
  prompt: string;
  response: string | null;
  promptTokens: number | null;
  responseTokens: number | null;
  costUsd: number | null;
  durationSeconds: number | null;
  createdAt: string;
}

interface Suggestion {
  id: string;
  userId: string;
  userEmail?: string;
  documentId: string | null;
  originalText: string;
  suggestionText: string;
  issueType: 'orthographic' | 'preciseness' | 'clarity' | 'consistency' | 'orthographic_consistency' | 'grammar' | 'spelling' | 'punctuation' | 'sentence_structure' | 'repetition' | 'overused_words' | 'cacophony' | 'passive_voice' | 'show_vs_tell' | 'pacing' | 'adverbs' | 'adjectives' | 'cliches' | 'transitions' | 'style' | 'strength' | 'user_edit';
  feedbackText: string;
  llmCallId: string | null;
  llmCall?: LLMCall | null;
  languageCode: string | null;
  languageName: string | null;
  shownAt: string;
  status: 'pending' | 'applied' | 'rejected' | 'reported';
  closedAt: string | null;
  createdAt: string;
}

interface CreditsActivity {
  id: string;
  balance: number;
  createdAt: string;
  usageType: string;
  notes: string | null;
  documentId: string | null;
  documentTitle: string | null;
  projectId: string | null;
  projectTitle: string | null;
}

interface SubscriptionHistoryEntry {
  id: string;
  stripeSubscriptionId: string;
  stripeCustomerId: string;
  stripePriceId: string | null;
  tier: 'free' | 'pro' | 'editorial';
  status: string;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;
  billingCurrency: 'eur' | 'usd' | null;
  billingInterval: 'month' | 'year' | null;
  createdAt: string;
}

interface UserDetailData {
  user: User;
  documents: Document[];
  suggestions: Suggestion[];
  subscriptionHistory: SubscriptionHistoryEntry[];
  stats: {
    documentsCount: number;
    projectCount: number;
    projects: Project[];
    storageUsage: {
      usedBytes: number;
      usedMb: number;
      quotaMb: number;
    };
    suggestionsStats: {
      total: number;
      applied: number;
      pending: number;
    };
  };
}

export const UserDetail: React.FC = () => {
  const { user, token, masterKey, impersonate, originalAdminUser } = useAuth();
  const { id } = useParams<{ id: string }>();
  const { t } = useLocale();
  const [impersonating, setImpersonating] = useState(false);
  const [loadingExampleProject, setLoadingExampleProject] = useState(false);
  const [data, setData] = useState<UserDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [fixedLlmModel, setFixedLlmModel] = useState<string>('');
  const [savingFixedModel, setSavingFixedModel] = useState(false);
  const [updatingAdmin, setUpdatingAdmin] = useState(false);
  const [updatingDisabled, setUpdatingDisabled] = useState(false);
  const [creditsAmount, setCreditsAmount] = useState<string>('');
  const [creditsNotes, setCreditsNotes] = useState<string>('');
  const [addingCredits, setAddingCredits] = useState(false);
  const [maxProjects, setMaxProjects] = useState<string>('');
  const [maxDocumentsPerProject, setMaxDocumentsPerProject] = useState<string>('');
  const [maxResourcesPerProject, setMaxResourcesPerProject] = useState<string>('');
  const [storageQuotaMb, setStorageQuotaMb] = useState<string>('');
  const [savingTierLimits, setSavingTierLimits] = useState(false);
  const [selectedTier, setSelectedTier] = useState<'free' | 'pro' | 'editorial'>('free');
  const [savingTier, setSavingTier] = useState(false);
  const [creditsBalance, setCreditsBalance] = useState<number>(0);
  const [creditsActivity, setCreditsActivity] = useState<CreditsActivity[]>([]);
  const [creditsActivityPage, setCreditsActivityPage] = useState<number>(1);
  const [creditsActivityTotal, setCreditsActivityTotal] = useState<number>(0);
  const [creditsActivityLoading, setCreditsActivityLoading] = useState<boolean>(false);
  const [onboardingEntries, setOnboardingEntries] = useState<Array<{
    kind: 'achievement' | 'reward';
    userId: string;
    entryId: string;
    pointsAwarded: number | null;
    rewardPayload: unknown | null;
    createdAt: string;
    deletedAt: string | null;
  }>>([]);
  const [onboardingPage, setOnboardingPage] = useState<number>(1);
  const [onboardingTotal, setOnboardingTotal] = useState<number>(0);
  const [onboardingLoading, setOnboardingLoading] = useState<boolean>(false);
  const [onboardingDeletingKey, setOnboardingDeletingKey] = useState<string | null>(null);
  const [onboardingRestoringKey, setOnboardingRestoringKey] = useState<string | null>(null);
  const onboardingPageSize = 10;
  const [suggestionsPage, setSuggestionsPage] = useState<number>(1);
  const suggestionsPageSize = 10;
  const [internalNote, setInternalNote] = useState<string>('');
  const [savingInternalNote, setSavingInternalNote] = useState(false);
  const [preferredLanguageCode, setPreferredLanguageCode] = useState<string>('spa');
  const [savingPreferredLanguage, setSavingPreferredLanguage] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion | null>(null);
  const [subscriptionHistoryExpanded, setSubscriptionHistoryExpanded] = useState<boolean>(false);
  const [resetStripeCustomerDialogOpen, setResetStripeCustomerDialogOpen] = useState(false);
  const [resettingStripeCustomerId, setResettingStripeCustomerId] = useState(false);

  useEffect(() => {
    const fetchUserDetail = async () => {
      if ((!token && !masterKey) || !user?.admin || !id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const url = masterKey
          ? `${API_BASE}/api/users/${id}?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/users/${id}`;
        const response = await fetch(url, {
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          if (response.status === 404) {
            setError('Usuario no encontrado');
          } else {
            throw new Error('Failed to fetch user details');
          }
          return;
        }

        const userData = await response.json();
        setData(userData);
        setFixedLlmModel(userData.user.fixedLlmModel || '');
        setSelectedTier(userData.user.tier);
        setMaxProjects(userData.user.maxProjects !== null ? String(userData.user.maxProjects) : '');
        setMaxDocumentsPerProject(userData.user.maxDocumentsPerProject !== null ? String(userData.user.maxDocumentsPerProject) : '');
        setMaxResourcesPerProject(userData.user.maxResourcesPerProject !== null ? String(userData.user.maxResourcesPerProject) : '');
        setStorageQuotaMb(userData.user.storageQuotaMb !== null ? String(userData.user.storageQuotaMb) : '');
        setInternalNote(userData.user.internalNote || '');
        setPreferredLanguageCode(userData.user.preferredLanguageCode || 'spa');
        setSuggestionsPage(1);
        setError(null);
      } catch (err) {
        console.error('Error fetching user details:', err);
        setError(t('failed_to_load_user_details') || 'Error al cargar detalles del usuario');
      } finally {
        setLoading(false);
      }
    };

    fetchUserDetail();
  }, [token, user, masterKey, id]);

  const fetchCreditsBalance = async () => {
    if (!id || (!token && !masterKey)) return;

    try {
      const url = masterKey
        ? `${API_BASE}/api/users/${id}/credits/balance?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/users/${id}/credits/balance`;
      const response = await fetch(url, {
        headers: {
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCreditsBalance(data.balance || 0);
      }
    } catch (err) {
      console.error('Error fetching credit balance:', err);
    }
  };

  const fetchCreditsActivity = async (page: number) => {
    if (!id || (!token && !masterKey)) return;

    setCreditsActivityLoading(true);
    try {
      const url = masterKey
        ? `${API_BASE}/api/admin-credits-activity?userId=${id}&page=${page}&pageSize=10&masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/admin-credits-activity?userId=${id}&page=${page}&pageSize=10`;
      const response = await fetch(url, {
        headers: {
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCreditsActivity(data.activity || []);
        setCreditsActivityTotal(data.pagination?.total || 0);
      }
    } catch (err) {
      console.error('Error fetching credits activity:', err);
    } finally {
      setCreditsActivityLoading(false);
    }
  };

  useEffect(() => {
    if (id && (token || masterKey) && user?.admin) {
      fetchCreditsBalance();
      fetchCreditsActivity(creditsActivityPage);
    }
  }, [id, token, masterKey, user, creditsActivityPage]);

  const fetchOnboardingEntries = async (page: number): Promise<void> => {
    if (!id || (!token && !masterKey)) return;

    setOnboardingLoading(true);
    try {
      const params = new URLSearchParams({
        userId: id,
        page: String(page),
        pageSize: String(onboardingPageSize),
        includeDeleted: 'true',
      });
      if (masterKey) {
        params.set('masterKey', masterKey);
      }
      const response = await fetch(`${API_BASE}/api/admin/onboarding?${params.toString()}`, {
        headers: {
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (response.ok) {
        const data = await response.json();
        setOnboardingEntries(data.entries || []);
        setOnboardingTotal(data.pagination?.total || 0);
      }
    } catch (err) {
      console.error('Error fetching onboarding entries:', err);
    } finally {
      setOnboardingLoading(false);
    }
  };

  useEffect(() => {
    if (id && (token || masterKey) && user?.admin) {
      void fetchOnboardingEntries(onboardingPage);
    }
  }, [id, token, masterKey, user, onboardingPage]);

  const softDeleteOnboardingEntry = async (entry: {
    kind: 'achievement' | 'reward';
    userId: string;
    entryId: string;
  }): Promise<void> => {
    if (!token && !masterKey) return;
    const key = `${entry.kind}:${entry.entryId}`;
    setOnboardingDeletingKey(key);
    try {
      const path =
        entry.kind === 'achievement'
          ? `/api/admin/onboarding/achievements/${entry.userId}/${encodeURIComponent(entry.entryId)}`
          : `/api/admin/onboarding/rewards/${entry.userId}/${encodeURIComponent(entry.entryId)}`;
      const url = masterKey
        ? `${API_BASE}${path}?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}${path}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) {
        throw new Error('Failed to delete onboarding entry');
      }
      await fetchOnboardingEntries(onboardingPage);
    } catch (err) {
      console.error('Error deleting onboarding entry:', err);
    } finally {
      setOnboardingDeletingKey(null);
    }
  };

  const restoreOnboardingEntry = async (entry: {
    kind: 'achievement' | 'reward';
    userId: string;
    entryId: string;
  }): Promise<void> => {
    if (!token && !masterKey) return;
    const key = `${entry.kind}:${entry.entryId}`;
    setOnboardingRestoringKey(key);
    try {
      const path =
        entry.kind === 'achievement'
          ? `/api/admin/onboarding/achievements/${entry.userId}/${encodeURIComponent(entry.entryId)}/restore`
          : `/api/admin/onboarding/rewards/${entry.userId}/${encodeURIComponent(entry.entryId)}/restore`;
      const url = masterKey
        ? `${API_BASE}${path}?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}${path}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) {
        throw new Error('Failed to restore onboarding entry');
      }
      await fetchOnboardingEntries(onboardingPage);
    } catch (err) {
      console.error('Error restoring onboarding entry:', err);
    } finally {
      setOnboardingRestoringKey(null);
    }
  };

  const handleSaveFixedModel = async () => {
    if (!id || (!token && !masterKey)) return;

    setSavingFixedModel(true);
    try {
      const url = masterKey
        ? `${API_BASE}/api/users/${id}?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/users/${id}`;
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ fixedLlmModel }),
      });

      if (!response.ok) {
        throw new Error('Failed to update user');
      }

      const updatedData = await response.json();
      if (data) {
        setData({
          ...data,
          user: updatedData.user,
        });
      }
    } catch (err) {
      console.error('Error updating fixed model:', err);
    } finally {
      setSavingFixedModel(false);
    }
  };

  const handleSaveInternalNote = async () => {
    if (!id || (!token && !masterKey)) return;

    setSavingInternalNote(true);
    try {
      const url = masterKey
        ? `${API_BASE}/api/users/${id}?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/users/${id}`;
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ internalNote: internalNote || null }),
      });

      if (!response.ok) {
        throw new Error('Failed to update internal note');
      }

      const updatedData = await response.json();
      if (data) {
        setData({
          ...data,
          user: updatedData.user,
        });
      }
    } catch (err) {
      console.error('Error updating internal note:', err);
      alert('Error al actualizar la nota interna');
    } finally {
      setSavingInternalNote(false);
    }
  };

  const handleSavePreferredLanguage = async () => {
    if (!id || (!token && !masterKey)) return;

    setSavingPreferredLanguage(true);
    try {
      const url = masterKey
        ? `${API_BASE}/api/users/${id}?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/users/${id}`;
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ preferredLanguageCode }),
      });

      if (!response.ok) {
        throw new Error('Failed to update preferred language');
      }

      const updatedData = await response.json();
      if (data) {
        setData({
          ...data,
          user: updatedData.user,
        });
      }
    } catch (err) {
      console.error('Error updating preferred language:', err);
      alert('Error al actualizar el idioma preferido');
    } finally {
      setSavingPreferredLanguage(false);
    }
  };

  const handleToggleDisabled = async () => {
    if (!data || !id || (!token && !masterKey) || updatingDisabled) {
      return;
    }
    const adminViewerId = originalAdminUser?.id ?? user?.id;
    if (!masterKey && adminViewerId === id && !data.user.disabled) {
      return;
    }

    setUpdatingDisabled(true);
    try {
      const url = masterKey
        ? `${API_BASE}/api/users/${id}?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/users/${id}`;
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ disabled: !data.user.disabled }),
      });

      if (!response.ok) {
        throw new Error('Failed to update user access');
      }

      const updatedData = await response.json();
      setData({
        ...data,
        user: updatedData.user,
      });
    } catch (err) {
      console.error('Error updating user access:', err);
    } finally {
      setUpdatingDisabled(false);
    }
  };

  const handleToggleAdmin = async () => {
    if (!data || !id || (!token && !masterKey) || updatingAdmin) {
      return;
    }

    setUpdatingAdmin(true);
    try {
      const url = masterKey
        ? `${API_BASE}/api/users/${id}?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/users/${id}`;
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ admin: !data.user.admin }),
      });

      if (!response.ok) {
        throw new Error('Failed to update user admin flag');
      }

      const updatedData = await response.json();
      setData({
        ...data,
        user: updatedData.user,
      });
    } catch (err) {
      console.error('Error updating admin flag:', err);
    } finally {
      setUpdatingAdmin(false);
    }
  };

  const handleAddCredits = async () => {
    if (!id || (!token && !masterKey) || addingCredits) {
      return;
    }

    const credits = parseInt(creditsAmount, 10);
    if (isNaN(credits)) {
      alert('Por favor, introduce un número válido de créditos');
      return;
    }

    setAddingCredits(true);
    try {
      const url = masterKey
        ? `${API_BASE}/api/users/${id}/credits?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/users/${id}/credits`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          credits,
          notes: creditsNotes || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add credits');
      }

      setCreditsAmount('');
      setCreditsNotes('');
      await fetchCreditsBalance();
      await fetchCreditsActivity(creditsActivityPage);
      alert(`Se han añadido ${credits} créditos correctamente`);
    } catch (err) {
      console.error('Error adding credits:', err);
      alert(err instanceof Error ? err.message : 'Error al añadir créditos');
    } finally {
      setAddingCredits(false);
    }
  };

  const handleSaveTier = async () => {
    if (!id || (!token && !masterKey) || savingTier || !data) {
      return;
    }

    if (selectedTier === data.user.tier) {
      return;
    }

    setSavingTier(true);
    try {
      const url = masterKey
        ? `${API_BASE}/api/users/${id}/tier?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/users/${id}/tier`;
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ tier: selectedTier }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update tier');
      }

      const updatedData = await response.json();
      setData({
        ...data,
        user: updatedData.user,
      });
      setMaxProjects(updatedData.user.maxProjects !== null ? String(updatedData.user.maxProjects) : '');
      setMaxDocumentsPerProject(updatedData.user.maxDocumentsPerProject !== null ? String(updatedData.user.maxDocumentsPerProject) : '');
      setMaxResourcesPerProject(updatedData.user.maxResourcesPerProject !== null ? String(updatedData.user.maxResourcesPerProject) : '');
      setStorageQuotaMb(updatedData.user.storageQuotaMb !== null ? String(updatedData.user.storageQuotaMb) : '');
      alert('Plan actualizado y límites aplicados correctamente');
    } catch (err) {
      console.error('Error updating tier:', err);
      alert(err instanceof Error ? err.message : 'Error al actualizar plan');
      setSelectedTier(data.user.tier);
    } finally {
      setSavingTier(false);
    }
  };

  const handleImpersonate = async () => {
    if (!id || (!token && !masterKey) || impersonating) {
      return;
    }

    if (!user?.admin) {
      alert('Solo los administradores pueden impersonar usuarios');
      return;
    }

    setImpersonating(true);
    try {
      await impersonate(id);
      window.location.href = '/';
    } catch (err) {
      console.error('Error impersonating user:', err);
      alert(err instanceof Error ? err.message : 'Error al impersonar usuario');
    } finally {
      setImpersonating(false);
    }
  };

  const handleLoadExampleProject = async () => {
    if (!id || (!token && !masterKey) || loadingExampleProject) {
      return;
    }

    const shouldContinue = window.confirm(
      t('confirm_load_example_project') || 'Esto creará una copia del proyecto de ejemplo para este usuario. ¿Continuar?'
    );
    if (!shouldContinue) {
      return;
    }

    setLoadingExampleProject(true);
    try {
      const url = masterKey
        ? `${API_BASE}/api/users/${id}/load-example-project?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/users/${id}/load-example-project`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || (t('example_project_loaded_error') || 'Error al cargar el proyecto de ejemplo'));
      }

      const result = await response.json();
      if (!result.created) {
        alert(t('example_project_loaded_error') || 'Error al cargar el proyecto de ejemplo');
        return;
      }

      alert(t('example_project_loaded_success') || 'Proyecto de ejemplo cargado correctamente');

      const detailsUrl = masterKey
        ? `${API_BASE}/api/users/${id}?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/users/${id}`;
      const detailsRes = await fetch(detailsUrl, {
        headers: {
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (detailsRes.ok) {
        const userData = await detailsRes.json();
        setData(userData);
      }
    } catch (err) {
      console.error('Error loading example project:', err);
      alert(err instanceof Error ? err.message : (t('example_project_loaded_error') || 'Error al cargar el proyecto de ejemplo'));
    } finally {
      setLoadingExampleProject(false);
    }
  };

  const handleResetStripeCustomerId = async (): Promise<void> => {
    if (!id || (!token && !masterKey) || resettingStripeCustomerId || !data?.user.stripeCustomerId) {
      return;
    }

    setResettingStripeCustomerId(true);
    try {
      const url = masterKey
        ? `${API_BASE}/api/users/${id}/reset-stripe-customer-id?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/users/${id}/reset-stripe-customer-id`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || t('admin_reset_stripe_customer_id_error'));
      }

      setData((current) => {
        if (!current) {
          return current;
        }
        return {
          ...current,
          user: {
            ...current.user,
            stripeCustomerId: null,
          },
        };
      });
      setResetStripeCustomerDialogOpen(false);
    } catch (err) {
      console.error('Error resetting Stripe customer ID:', err);
      alert(err instanceof Error ? err.message : t('admin_reset_stripe_customer_id_error'));
    } finally {
      setResettingStripeCustomerId(false);
    }
  };

  const handleSaveTierLimits = async () => {
    if (!id || (!token && !masterKey) || savingTierLimits || !data) {
      return;
    }

    const maxProjectsValue = maxProjects === '' ? null : parseInt(maxProjects, 10);
    const maxDocumentsPerProjectValue = maxDocumentsPerProject === '' ? null : parseInt(maxDocumentsPerProject, 10);
    const maxResourcesPerProjectValue = maxResourcesPerProject === '' ? null : parseInt(maxResourcesPerProject, 10);
    const storageQuotaMbValue = storageQuotaMb === '' ? null : parseInt(storageQuotaMb, 10);

    if (maxProjectsValue !== null && (isNaN(maxProjectsValue) || maxProjectsValue <= 0)) {
      alert('Por favor, introduce un número válido para Máximo de Proyectos (o déjalo vacío para ilimitado)');
      return;
    }
    if (maxDocumentsPerProjectValue !== null && (isNaN(maxDocumentsPerProjectValue) || maxDocumentsPerProjectValue <= 0)) {
      alert('Por favor, introduce un número válido para Máximo de Documentos por Proyecto (o déjalo vacío para ilimitado)');
      return;
    }
    if (maxResourcesPerProjectValue !== null && (isNaN(maxResourcesPerProjectValue) || maxResourcesPerProjectValue <= 0)) {
      alert('Por favor, introduce un número válido para Máximo de Recursos por Proyecto (o déjalo vacío para ilimitado)');
      return;
    }
    if (storageQuotaMbValue !== null && (isNaN(storageQuotaMbValue) || storageQuotaMbValue <= 0)) {
      alert('Por favor, introduce un número válido para Cuota de Almacenamiento (o déjalo vacío para ilimitado)');
      return;
    }

    setSavingTierLimits(true);
    try {
      const url = masterKey
        ? `${API_BASE}/api/users/${id}/tier-limits?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/users/${id}/tier-limits`;
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          maxProjects: maxProjectsValue,
          maxDocumentsPerProject: maxDocumentsPerProjectValue,
          maxResourcesPerProject: maxResourcesPerProjectValue,
          storageQuotaMb: storageQuotaMbValue,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update tier limits');
      }

      const updatedData = await response.json();
      setData({
        ...data,
        user: updatedData.user,
      });
      alert('Límites de plan actualizados correctamente');
    } catch (err) {
      console.error('Error updating tier limits:', err);
      alert(err instanceof Error ? err.message : 'Error al actualizar límites de plan');
    } finally {
      setSavingTierLimits(false);
    }
  };

  const activeDocuments = data?.documents.filter(doc => !doc.trashed) || [];
  const trashedDocuments = data?.documents.filter(doc => doc.trashed) || [];

  const projectTitleById = useMemo(() => {
    const map = new Map<string, string>();
    data?.stats.projects.forEach(project => map.set(project.id, project.title));
    return map;
  }, [data?.stats.projects]);

  const documentsByLastEdition = useMemo(() => {
    return [...(data?.documents || [])].sort(
      (a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime()
    );
  }, [data?.documents]);

  const getDocumentList = useCallback((): Document[] => {
    if (!selectedDocument) return [];
    return selectedDocument.trashed ? trashedDocuments : activeDocuments;
  }, [selectedDocument, activeDocuments, trashedDocuments]);

  const getCurrentIndex = useCallback((): number => {
    if (!selectedDocument) return -1;
    const list = getDocumentList();
    return list.findIndex(doc => doc.id === selectedDocument.id);
  }, [selectedDocument, getDocumentList]);

  const navigateToDocument = useCallback((direction: 'prev' | 'next'): void => {
    const list = getDocumentList();
    const currentIndex = getCurrentIndex();
    
    if (currentIndex === -1) return;

    let targetIndex: number;
    if (direction === 'prev') {
      targetIndex = currentIndex - 1;
    } else {
      targetIndex = currentIndex + 1;
    }

    if (targetIndex < 0 || targetIndex >= list.length) return;

    setSelectedDocument(list[targetIndex]);
  }, [getDocumentList, getCurrentIndex]);

  const currentIndex = getCurrentIndex();
  const documentList = getDocumentList();
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < documentList.length - 1;

  useEffect(() => {
    if (!selectedDocument) return;

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) {
        return;
      }

      if (e.key === 'ArrowLeft' && hasPrevious) {
        e.preventDefault();
        navigateToDocument('prev');
      } else if (e.key === 'ArrowRight' && hasNext) {
        e.preventDefault();
        navigateToDocument('next');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedDocument, hasPrevious, hasNext, navigateToDocument]);

  const getSuggestionList = useCallback((): Suggestion[] => {
    if (!data) return [];
    return data.suggestions.slice((suggestionsPage - 1) * suggestionsPageSize, suggestionsPage * suggestionsPageSize);
  }, [data, suggestionsPage, suggestionsPageSize]);

  const getSuggestionIndex = useCallback((): number => {
    if (!selectedSuggestion) return -1;
    const list = getSuggestionList();
    return list.findIndex(s => s.id === selectedSuggestion.id);
  }, [selectedSuggestion, getSuggestionList]);

  const navigateToSuggestion = useCallback((direction: 'prev' | 'next'): void => {
    if (!data) return;
    
    const list = getSuggestionList();
    const currentIndex = getSuggestionIndex();
    
    if (currentIndex === -1) return;

    let targetIndex: number;
    if (direction === 'prev') {
      targetIndex = currentIndex - 1;
      if (targetIndex < 0) {
        if (suggestionsPage > 1) {
          const newPage = suggestionsPage - 1;
          setSuggestionsPage(newPage);
          const newList = data.suggestions.slice((newPage - 1) * suggestionsPageSize, newPage * suggestionsPageSize);
          if (newList.length > 0) {
            setSelectedSuggestion(newList[newList.length - 1]);
          }
        }
        return;
      }
    } else {
      targetIndex = currentIndex + 1;
      if (targetIndex >= list.length) {
        const totalPages = Math.ceil(data.suggestions.length / suggestionsPageSize);
        if (suggestionsPage < totalPages) {
          const newPage = suggestionsPage + 1;
          setSuggestionsPage(newPage);
          const newList = data.suggestions.slice((newPage - 1) * suggestionsPageSize, newPage * suggestionsPageSize);
          if (newList.length > 0) {
            setSelectedSuggestion(newList[0]);
          }
        }
        return;
      }
    }

    setSelectedSuggestion(list[targetIndex]);
  }, [getSuggestionList, getSuggestionIndex, suggestionsPage, suggestionsPageSize, data]);

  const suggestionIndex = getSuggestionIndex();
  const suggestionList = getSuggestionList();
  const hasPreviousSuggestion = suggestionIndex > 0 || suggestionsPage > 1;
  const hasNextSuggestion = suggestionIndex < suggestionList.length - 1 || suggestionsPage < Math.ceil((data?.suggestions.length || 0) / suggestionsPageSize);

  useEffect(() => {
    if (!selectedSuggestion) return;

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) {
        return;
      }

      if (e.key === 'ArrowLeft' && hasPreviousSuggestion) {
        e.preventDefault();
        navigateToSuggestion('prev');
      } else if (e.key === 'ArrowRight' && hasNextSuggestion) {
        e.preventDefault();
        navigateToSuggestion('next');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedSuggestion, hasPreviousSuggestion, hasNextSuggestion, navigateToSuggestion]);

  const getIssueTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      orthographic: 'Ortográfico',
      preciseness: 'Precisión',
      clarity: 'Claridad',
      consistency: 'Consistencia',
      orthographic_consistency: 'Consistencia Ortográfica',
      grammar: 'Gramática',
      spelling: 'Ortografía',
      punctuation: 'Puntuación',
      sentence_structure: 'Estructura de Oración',
      repetition: 'Repetición',
      overused_words: 'Palabras Sobreutilizadas',
      cacophony: 'Cacofonía',
      passive_voice: 'Voz Pasiva',
      show_vs_tell: 'Mostrar vs Contar',
      pacing: 'Ritmo',
      adverbs: 'Adverbios',
      adjectives: 'Adjetivos',
      cliches: 'Clichés',
      transitions: 'Transiciones',
      style: 'Estilo',
      strength: 'Fortaleza',
      other: 'Otro',
    };
    return labels[type] || type;
  };

  const getStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      pending: 'Pendiente',
      applied: 'Aprobada',
      rejected: 'Rechazada',
      reported: 'Reportada',
      'error:originalTextNotFound': 'Error: Texto original no encontrado',
    };
    return labels[status] || status;
  };

  if (!user || !user.admin) {
    return <AdminAccessDenied />;
  }

  if (loading) {
    return <AdminLoadingState />;
  }

  if (error || !data) {
    return (
      <div className={`${ADMIN_PAGE_CLASSNAME} py-8 text-center`}>
        <h1 className="mb-4 text-xl font-semibold text-foreground">{error || 'Error'}</h1>
        <Button asChild>
          <Link to="/admin/users">Volver a Usuarios</Link>
        </Button>
      </div>
    );
  }

  const appliedRate = data.stats.suggestionsStats.total > 0
    ? ((data.stats.suggestionsStats.applied / data.stats.suggestionsStats.total) * 100).toFixed(1)
    : '0';

  return (
    <>
      <div className={ADMIN_PAGE_CLASSNAME}>
        <div className={ADMIN_PANEL_CLASSNAME}>
          {data.user.deletedAt && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>
                This account was deleted on {new Date(data.user.deletedAt).toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </AlertDescription>
            </Alert>
          )}
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="m-0 text-lg font-semibold text-foreground">
              Información del Usuario
            </h2>
            {user?.admin && (
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  type="button"
                  onClick={handleLoadExampleProject}
                  disabled={loadingExampleProject || !!data.user.deletedAt}
                >
                  {loadingExampleProject
                    ? (t('loading_example_project') || 'Cargando proyecto de ejemplo...')
                    : (t('load_example_project') || 'Cargar proyecto de ejemplo')}
                </Button>

                {!originalAdminUser && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleImpersonate}
                    disabled={impersonating}
                  >
                    {impersonating ? 'Impersonando...' : 'Impersonar Usuario'}
                  </Button>
                )}
              </div>
            )}
          </div>
          <div className="grid w-full min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 [&>div]:min-w-0">
            <div>
              <div className={`mb-1 ${ADMIN_MUTED_TEXT_CLASSNAME}`}>Email</div>
              <div className="break-all text-base font-medium text-foreground">{data.user.email}</div>
            </div>
            <div>
              <div className={`mb-1 ${ADMIN_MUTED_TEXT_CLASSNAME}`}>Administrador</div>
              <div className="flex flex-wrap items-center gap-3">
                <div>
                  {data.user.admin ? (
                    <span className={ADMIN_BADGE_INFO_CLASSNAME}>Sí</span>
                  ) : (
                    <span className={ADMIN_BADGE_NEUTRAL_CLASSNAME}>No</span>
                  )}
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant={data.user.admin ? 'outline' : 'default'}
                  onClick={handleToggleAdmin}
                  disabled={updatingAdmin}
                >
                  {updatingAdmin
                    ? t('updating_admin')
                    : data.user.admin
                    ? t('revoke_admin')
                    : t('grant_admin')}
                </Button>
              </div>
            </div>
            <div>
              <div className={`mb-1 ${ADMIN_MUTED_TEXT_CLASSNAME}`}>
                {t('account_access')}
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <div>
                  {data.user.disabled ? (
                    <span className={ADMIN_BADGE_DANGER_CLASSNAME}>
                      {t('account_access_blocked')}
                    </span>
                  ) : (
                    <span className={ADMIN_BADGE_SUCCESS_CLASSNAME}>
                      {t('account_access_active')}
                    </span>
                  )}
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant={data.user.disabled ? 'default' : 'destructive'}
                  onClick={handleToggleDisabled}
                  disabled={
                    updatingDisabled ||
                    (!masterKey && (originalAdminUser?.id ?? user?.id) === id && !data.user.disabled)
                  }
                >
                  {updatingDisabled
                    ? t('updating_user_access')
                    : data.user.disabled
                      ? t('enable_user_account')
                      : t('disable_user_account')}
                </Button>
              </div>
            </div>
            <div>
              <div className={`mb-1 ${ADMIN_MUTED_TEXT_CLASSNAME}`}>Fecha de Creación</div>
              <div className={ADMIN_BODY_TEXT_CLASSNAME}>
                {new Date(data.user.createdAt).toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>
            </div>
            <div>
              <div className={`mb-1 ${ADMIN_MUTED_TEXT_CLASSNAME}`}>Última Actualización</div>
              <div className={ADMIN_BODY_TEXT_CLASSNAME}>
                {new Date(data.user.updatedAt).toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>
            </div>
            <div>
              <div className={`mb-1 ${ADMIN_MUTED_TEXT_CLASSNAME}`}>Código de Invitación Usado</div>
              <div className={ADMIN_BODY_TEXT_CLASSNAME}>
                {data.user.invitationCodeUsed || '-'}
              </div>
            </div>
            <div>
              <div className={`mb-2 ${ADMIN_MUTED_TEXT_CLASSNAME}`}>Idioma Preferido</div>
              <div className="flex flex-wrap items-center gap-3">
                <Select value={preferredLanguageCode} onValueChange={setPreferredLanguageCode}>
                  <SelectTrigger className="w-full max-w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="eng">English</SelectItem>
                    <SelectItem value="spa">Spanish</SelectItem>
                    <SelectItem value="cat">Catalan</SelectItem>
                    <SelectItem value="fra">French</SelectItem>
                    <SelectItem value="deu">German</SelectItem>
                    <SelectItem value="ita">Italian</SelectItem>
                    <SelectItem value="por">Portuguese</SelectItem>
                    <SelectItem value="rus">Russian</SelectItem>
                    <SelectItem value="jpn">Japanese</SelectItem>
                    <SelectItem value="kor">Korean</SelectItem>
                    <SelectItem value="zho">Chinese</SelectItem>
                    <SelectItem value="ara">Arabic</SelectItem>
                    <SelectItem value="hin">Hindi</SelectItem>
                    <SelectItem value="nld">Dutch</SelectItem>
                    <SelectItem value="pol">Polish</SelectItem>
                    <SelectItem value="tur">Turkish</SelectItem>
                    <SelectItem value="swe">Swedish</SelectItem>
                    <SelectItem value="nor">Norwegian</SelectItem>
                    <SelectItem value="dan">Danish</SelectItem>
                    <SelectItem value="fin">Finnish</SelectItem>
                    <SelectItem value="ron">Romanian</SelectItem>
                    <SelectItem value="ces">Czech</SelectItem>
                    <SelectItem value="ell">Greek</SelectItem>
                    <SelectItem value="heb">Hebrew</SelectItem>
                    <SelectItem value="tha">Thai</SelectItem>
                    <SelectItem value="vie">Vietnamese</SelectItem>
                    <SelectItem value="ind">Indonesian</SelectItem>
                    <SelectItem value="msa">Malay</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  onClick={handleSavePreferredLanguage}
                  disabled={savingPreferredLanguage || preferredLanguageCode === (data.user.preferredLanguageCode || 'spa')}
                >
                  {savingPreferredLanguage ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
            </div>
            <div>
              <div className={`mb-2 ${ADMIN_MUTED_TEXT_CLASSNAME}`}>Plan</div>
              <div className="flex flex-wrap items-center gap-3">
                <Select value={selectedTier} onValueChange={(value) => setSelectedTier(value as 'free' | 'pro' | 'editorial')}>
                  <SelectTrigger className="w-full max-w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Gratis</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="editorial">Editorial</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  onClick={handleSaveTier}
                  disabled={savingTier || selectedTier === data.user.tier}
                >
                  {savingTier ? 'Guardando...' : 'Guardar y aplicar'}
                </Button>
              </div>
            </div>
          </div>

          <div className={ADMIN_DIVIDER_CLASSNAME}>
            <h3 className="mb-4 text-base font-semibold text-foreground">
              Modelo LLM Fijo
            </h3>
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <LLMModelSelector
                value={fixedLlmModel}
                onChange={setFixedLlmModel}
                emptyLabel="Sin fijar (usar reglas normales)"
                className="w-full min-w-0 sm:min-w-[250px] sm:flex-1"
              />
              <Button
                type="button"
                onClick={handleSaveFixedModel}
                disabled={savingFixedModel || fixedLlmModel === (data.user.fixedLlmModel || '')}
              >
                {savingFixedModel ? 'Guardando...' : 'Guardar'}
              </Button>
              {data.user.fixedLlmModel && (
                <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
                  ✓ Modelo fijo activo
                </span>
              )}
            </div>
            <p className={`mt-2 text-xs ${ADMIN_MUTED_TEXT_CLASSNAME}`}>
              Si se establece, este usuario usará siempre el modelo seleccionado en lugar de las reglas normales de selección.
            </p>
          </div>

          <div className={ADMIN_DIVIDER_CLASSNAME}>
            <h3 className="mb-4 text-base font-semibold text-foreground">
              Nota Interna
            </h3>
            <div className="flex flex-col gap-3">
              <textarea
                value={internalNote}
                onChange={(e) => setInternalNote(e.target.value)}
                placeholder={t('user_internal_note_placeholder')}
                rows={4}
                className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
              />
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  onClick={handleSaveInternalNote}
                  disabled={savingInternalNote || internalNote === (data.user.internalNote || '')}
                >
                  {savingInternalNote ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
              <p className={`text-xs ${ADMIN_MUTED_TEXT_CLASSNAME}`}>
                Esta nota es solo visible para administradores y nunca se expone al usuario a través de la API.
              </p>
            </div>
          </div>
        </div>

        <div className={ADMIN_PANEL_CLASSNAME}>
          <h2 className={ADMIN_PANEL_TITLE_CLASSNAME}>
            Límites de Plan
          </h2>
          <div className="flex flex-col gap-3">
            <div className="grid w-full min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 [&>div]:min-w-0">
                <div>
                  <Label htmlFor="max-projects" className={ADMIN_FIELD_LABEL_CLASSNAME}>
                    Máximo de Proyectos
                  </Label>
                  <Input
                    id="max-projects"
                    type="number"
                    value={maxProjects}
                    onChange={(e) => setMaxProjects(e.target.value)}
                    placeholder={t('unlimited_placeholder')}
                    min="1"
                  />
                  <p className={`mt-1 text-xs ${ADMIN_MUTED_TEXT_CLASSNAME}`}>
                    Dejar vacío para ilimitado
                  </p>
                </div>
                <div>
                  <Label htmlFor="max-documents-per-project" className={ADMIN_FIELD_LABEL_CLASSNAME}>
                    Máximo de Documentos por Proyecto
                  </Label>
                  <Input
                    id="max-documents-per-project"
                    type="number"
                    value={maxDocumentsPerProject}
                    onChange={(e) => setMaxDocumentsPerProject(e.target.value)}
                    placeholder={t('unlimited_placeholder')}
                    min="1"
                  />
                  <p className={`mt-1 text-xs ${ADMIN_MUTED_TEXT_CLASSNAME}`}>
                    Dejar vacío para ilimitado
                  </p>
                </div>
                <div>
                  <Label htmlFor="max-resources-per-project" className={ADMIN_FIELD_LABEL_CLASSNAME}>
                    Máximo de Recursos por Proyecto
                  </Label>
                  <Input
                    id="max-resources-per-project"
                    type="number"
                    value={maxResourcesPerProject}
                    onChange={(e) => setMaxResourcesPerProject(e.target.value)}
                    placeholder={t('unlimited_placeholder')}
                    min="1"
                  />
                  <p className={`mt-1 text-xs ${ADMIN_MUTED_TEXT_CLASSNAME}`}>
                    Dejar vacío para ilimitado
                  </p>
                </div>
                <div>
                  <Label htmlFor="storage-quota-mb" className={ADMIN_FIELD_LABEL_CLASSNAME}>
                    Cuota de Almacenamiento (MB)
                  </Label>
                  <Input
                    id="storage-quota-mb"
                    type="number"
                    value={storageQuotaMb}
                    onChange={(e) => setStorageQuotaMb(e.target.value)}
                    placeholder={t('unlimited_placeholder')}
                    min="1"
                  />
                  <p className={`mt-1 text-xs ${ADMIN_MUTED_TEXT_CLASSNAME}`}>
                    Dejar vacío para ilimitado
                  </p>
                  {data && (
                    <p
                      className={cn(
                        'mt-2 text-xs',
                        data.stats.storageUsage.usedMb > data.stats.storageUsage.quotaMb
                          ? 'font-medium text-red-600 dark:text-red-400'
                          : ADMIN_MUTED_TEXT_CLASSNAME,
                      )}
                    >
                      Uso actual: {data.stats.storageUsage.usedMb.toFixed(2)} MB / {data.stats.storageUsage.quotaMb} MB
                    </p>
                  )}
                </div>
            </div>
            <div className="flex items-center gap-3">
                <Button
                  type="button"
                  onClick={handleSaveTierLimits}
                  disabled={savingTierLimits}
                >
                  {savingTierLimits ? 'Guardando...' : 'Guardar Límites'}
                </Button>
            </div>
          </div>
        </div>

        <div className={ADMIN_PANEL_CLASSNAME}>
          <h2 className={ADMIN_PANEL_TITLE_CLASSNAME}>
            Stripe Subscription
          </h2>
          <div className="mb-6 grid w-full min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 [&>div]:min-w-0">
            <div>
              <div className={`mb-1 ${ADMIN_MUTED_TEXT_CLASSNAME}`}>Stripe Customer ID</div>
              <div className="break-all font-mono text-sm text-foreground">
                {data.user.stripeCustomerId ? (
                  <a
                    href={`https://dashboard.stripe.com/customers/${data.user.stripeCustomerId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline-offset-2 hover:underline"
                  >
                    {data.user.stripeCustomerId}
                  </a>
                ) : '-'}
              </div>
              {data.user.stripeCustomerId && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setResetStripeCustomerDialogOpen(true)}
                  className="mt-3"
                >
                  {t('admin_reset_stripe_customer_id')}
                </Button>
              )}
            </div>
            <div>
              <div className={`mb-1 ${ADMIN_MUTED_TEXT_CLASSNAME}`}>Stripe Subscription ID</div>
              <div className="break-all font-mono text-sm text-foreground">{data.user.stripeSubscriptionId || '-'}</div>
            </div>
            <div>
              <div className={`mb-1 ${ADMIN_MUTED_TEXT_CLASSNAME}`}>Subscription Status</div>
              <div className={ADMIN_BODY_TEXT_CLASSNAME}>
                {data.user.subscriptionStatus ? (
                  <span
                    className={
                      data.user.subscriptionStatus === 'active' || data.user.subscriptionStatus === 'trialing'
                        ? ADMIN_BADGE_SUCCESS_CLASSNAME
                        : data.user.subscriptionStatus === 'past_due' || data.user.subscriptionStatus === 'unpaid'
                          ? ADMIN_BADGE_DANGER_CLASSNAME
                          : ADMIN_BADGE_NEUTRAL_CLASSNAME
                    }
                  >
                    {data.user.subscriptionStatus}
                  </span>
                ) : '-'}
              </div>
            </div>
            <div>
              <div className={`mb-1 ${ADMIN_MUTED_TEXT_CLASSNAME}`}>Current Period Start</div>
              <div className={ADMIN_BODY_TEXT_CLASSNAME}>
                {data.user.subscriptionCurrentPeriodStart ? new Date(data.user.subscriptionCurrentPeriodStart).toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                }) : '-'}
              </div>
            </div>
            <div>
              <div className={`mb-1 ${ADMIN_MUTED_TEXT_CLASSNAME}`}>Current Period End</div>
              <div className={ADMIN_BODY_TEXT_CLASSNAME}>
                {data.user.subscriptionCurrentPeriodEnd ? new Date(data.user.subscriptionCurrentPeriodEnd).toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                }) : '-'}
              </div>
            </div>
            <div>
              <div className={`mb-1 ${ADMIN_MUTED_TEXT_CLASSNAME}`}>Cancel at Period End</div>
              <div className={ADMIN_BODY_TEXT_CLASSNAME}>
                {data.user.subscriptionCancelAtPeriodEnd ? (
                  <span className={ADMIN_BADGE_DANGER_CLASSNAME}>Sí</span>
                ) : 'No'}
              </div>
            </div>
            <div>
              <div className={`mb-1 ${ADMIN_MUTED_TEXT_CLASSNAME}`}>Billing Currency</div>
              <div className={ADMIN_BODY_TEXT_CLASSNAME}>{data.user.billingCurrency ? data.user.billingCurrency.toUpperCase() : '-'}</div>
            </div>
            <div>
              <div className={`mb-1 ${ADMIN_MUTED_TEXT_CLASSNAME}`}>Billing Interval</div>
              <div className={ADMIN_BODY_TEXT_CLASSNAME}>
                {data.user.billingInterval === 'month' ? 'Mensual' : data.user.billingInterval === 'year' ? 'Anual' : '-'}
              </div>
            </div>
          </div>

          {data.subscriptionHistory && data.subscriptionHistory.length > 0 && (
            <div className={ADMIN_DIVIDER_CLASSNAME}>
              <button
                type="button"
                onClick={() => setSubscriptionHistoryExpanded(!subscriptionHistoryExpanded)}
                className={cn(
                  'mb-0 flex cursor-pointer items-center gap-2 border-0 bg-transparent p-0',
                  subscriptionHistoryExpanded && 'mb-4',
                )}
              >
                <h3 className="m-0 text-base font-semibold text-foreground">
                  Subscription History ({data.subscriptionHistory.length})
                </h3>
                <span className={ADMIN_MUTED_TEXT_CLASSNAME}>
                  {subscriptionHistoryExpanded ? '▼' : '▶'}
                </span>
              </button>
              {subscriptionHistoryExpanded && (
                <AdminTableWrap>
                  <table className={cn(ADMIN_TABLE_CLASSNAME, "min-w-[900px]")}>
                    <thead>
                      <tr>
                        <th>
                          Date
                        </th>
                        <th>
                          Tier
                        </th>
                        <th>
                          Status
                        </th>
                        <th>
                          Period
                        </th>
                        <th>
                          Billing
                        </th>
                        <th>
                          Cancel at End
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.subscriptionHistory.map((entry) => (
                        <tr
                          key={entry.id}
                        >
                          <td>
                            {new Date(entry.createdAt).toLocaleDateString('es-ES', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </td>
                          <td>
                            <span
                              className={cn(
                                'inline-block rounded-full px-2 py-0.5 text-[0.7rem] font-medium capitalize',
                                entry.tier === 'pro'
                                  ? 'bg-blue-100 text-blue-900 dark:bg-blue-950 dark:text-blue-100'
                                  : entry.tier === 'editorial'
                                    ? 'bg-purple-100 text-purple-900 dark:bg-purple-950 dark:text-purple-100'
                                    : 'bg-muted text-muted-foreground',
                              )}
                            >
                              {entry.tier}
                            </span>
                          </td>
                          <td>
                            <span
                              className={
                                entry.status === 'active' || entry.status === 'trialing'
                                  ? ADMIN_BADGE_SUCCESS_CLASSNAME
                                  : entry.status === 'past_due' || entry.status === 'unpaid' || entry.status === 'canceled'
                                    ? ADMIN_BADGE_DANGER_CLASSNAME
                                    : ADMIN_BADGE_NEUTRAL_CLASSNAME
                              }
                            >
                              {entry.status}
                            </span>
                          </td>
                          <td className={ADMIN_TABLE_CELL_MUTED_CLASSNAME}>
                            {entry.currentPeriodStart && entry.currentPeriodEnd ? (
                              <>
                                {new Date(entry.currentPeriodStart).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' })}
                                {' - '}
                                {new Date(entry.currentPeriodEnd).toLocaleDateString('es-ES', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </>
                            ) : '-'}
                          </td>
                          <td>
                            {entry.billingInterval === 'month' ? 'Mensual' : entry.billingInterval === 'year' ? 'Anual' : '-'}
                            {entry.billingCurrency ? ` (${entry.billingCurrency.toUpperCase()})` : ''}
                          </td>
                          <td>
                            {entry.cancelAtPeriodEnd ? (
                              <span className="font-medium text-red-600 dark:text-red-400">Yes</span>
                            ) : (
                              <span className="text-muted-foreground">No</span>
                            )}
                            {entry.canceledAt && (
                              <div className="mt-1 text-[0.65rem] text-red-600 dark:text-red-400">
                                Canceled: {new Date(entry.canceledAt).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' })}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </AdminTableWrap>
              )}
            </div>
          )}
        </div>

        <div className={ADMIN_PANEL_CLASSNAME}>
          <h2 className={ADMIN_SECTION_TITLE_CLASSNAME}>
            {t('admin_onboarding_title')}
          </h2>
          {onboardingLoading ? (
            <div className="py-8 text-center text-muted-foreground">{t('loading')}</div>
          ) : onboardingEntries.length === 0 ? (
            <div className={ADMIN_EMPTY_BOX_CLASSNAME}>
              {t('admin_onboarding_empty')}
            </div>
          ) : (
            <>
              <AdminTableWrap>
<table className={cn(ADMIN_TABLE_CLASSNAME, "min-w-[700px]")}>
                    <thead>
                      <tr>
                        <th>
                          {t('admin_onboarding_col_kind')}
                        </th>
                        <th>
                          {t('admin_onboarding_col_id')}
                        </th>
                        <th>
                          {t('admin_onboarding_col_details')}
                        </th>
                        <th>
                          {t('admin_onboarding_col_date')}
                        </th>
                        <th>
                          {t('admin_onboarding_col_actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {onboardingEntries.map((entry) => {
                        const rowKey = `${entry.kind}:${entry.entryId}`;
                        const labelKey =
                          entry.kind === 'achievement'
                            ? `onboarding_action_${entry.entryId}`
                            : `onboarding_milestone_${entry.entryId}`;
                        return (
                          <tr
                            key={rowKey}
                            className={entry.deletedAt ? "opacity-50" : undefined}
                          >
                            <td>
                              {entry.kind === 'achievement'
                                ? t('admin_onboarding_kind_achievement')
                                : t('admin_onboarding_kind_reward')}
                              {entry.deletedAt ? ` (${t('admin_onboarding_deleted')})` : ''}
                            </td>
                            <td>
                              <div>{t(labelKey)}</div>
                              <div className="text-xs text-muted-foreground">{entry.entryId}</div>
                            </td>
                            <td className={ADMIN_TABLE_CELL_MUTED_CLASSNAME}>
                              {entry.kind === 'achievement'
                                ? `+${entry.pointsAwarded ?? 0} pts`
                                : JSON.stringify(entry.rewardPayload)}
                            </td>
                            <td>
                              {new Date(entry.createdAt).toLocaleDateString('es-ES', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </td>
                            <td>
                              {!entry.deletedAt && (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="border-red-200 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-800 dark:bg-red-950 dark:text-red-200 dark:hover:bg-red-900"
                                  disabled={onboardingDeletingKey === rowKey || onboardingRestoringKey !== null}
                                  onClick={() => void softDeleteOnboardingEntry(entry)}
                                >
                                  {t('admin_onboarding_soft_delete')}
                                </Button>
                              )}
                              {entry.deletedAt && (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200 dark:hover:bg-emerald-900"
                                  disabled={onboardingRestoringKey === rowKey || onboardingDeletingKey !== null}
                                  onClick={() => void restoreOnboardingEntry(entry)}
                                >
                                  {t('admin_onboarding_restore')}
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </AdminTableWrap>
              {Math.ceil(onboardingTotal / onboardingPageSize) > 1 && (
                <AdminPaginationBar
                  page={onboardingPage}
                  totalPages={Math.ceil(onboardingTotal / onboardingPageSize)}
                  onPageChange={setOnboardingPage}
                  alwaysShow
                />
              )}
            </>
          )}
        </div>

        <div className={ADMIN_PANEL_CLASSNAME}>
          <h2 className={ADMIN_SECTION_TITLE_CLASSNAME}>
            Credits
          </h2>

          <div className="mb-6">
            <div className="inline-block rounded-lg border border-border bg-muted/40 px-6 py-4">
              <div className={`mb-2 ${ADMIN_MUTED_TEXT_CLASSNAME}`}>Current Balance</div>
              <div className={ADMIN_STAT_VALUE_CLASSNAME}>{creditsBalance}</div>
            </div>
          </div>

          <UserCreditsActivityChart userId={id!} />

          <div className="mb-6">
            <h3 className="mb-4 text-base font-semibold text-foreground">
              Credits Activity
            </h3>
            {creditsActivityLoading ? (
              <div className="py-8 text-center text-muted-foreground">Loading...</div>
            ) : creditsActivity.length === 0 ? (
              <div className={ADMIN_EMPTY_BOX_CLASSNAME}>
                No credits activity
              </div>
            ) : (
              <>
                <AdminTableWrap>
                  <table className={cn(ADMIN_TABLE_CLASSNAME, "min-w-[800px]")}>
                      <thead>
                        <tr>
                          <th>
                            Date
                          </th>
                          <th>
                            Balance
                          </th>
                          <th>
                            Type
                          </th>
                          <th>
                            Notes
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {creditsActivity.map((activity) => (
                          <tr
                            key={activity.id}
                          >
                            <td>
                              {new Date(activity.createdAt).toLocaleDateString('es-ES', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </td>
                            <td
                              className={cn(
                                'font-medium',
                                activity.balance >= 0
                                  ? 'text-emerald-600 dark:text-emerald-400'
                                  : 'text-red-600 dark:text-red-400',
                              )}
                            >
                              {activity.balance >= 0 ? '+' : ''}{activity.balance}
                            </td>
                            <td>
                              {activity.usageType}
                            </td>
                            <td className={ADMIN_TABLE_CELL_MUTED_CLASSNAME}>
                              {activity.notes || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </AdminTableWrap>
                <AdminPaginationBar
                  page={creditsActivityPage}
                  totalPages={Math.ceil(creditsActivityTotal / 10)}
                  onPageChange={setCreditsActivityPage}
                />
              </>
            )}
          </div>

          <div className={ADMIN_DIVIDER_CLASSNAME}>
            <h3 className="mb-4 text-base font-semibold text-foreground">
              Add credits
            </h3>
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-start gap-3">
                <div className="min-w-0 flex-1 basis-[200px]">
                  <Label htmlFor="credits-amount" className={ADMIN_FIELD_LABEL_CLASSNAME}>
                    Créditos
                  </Label>
                  <Input
                    id="credits-amount"
                    type="number"
                    value={creditsAmount}
                    onChange={(e) => setCreditsAmount(e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div className="min-w-0 flex-[2] basis-[250px]">
                  <Label htmlFor="credits-notes" className={ADMIN_FIELD_LABEL_CLASSNAME}>
                    Notas (opcional)
                  </Label>
                  <Input
                    id="credits-notes"
                    type="text"
                    value={creditsNotes}
                    onChange={(e) => setCreditsNotes(e.target.value)}
                    placeholder={t('credit_assignment_notes_placeholder')}
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    type="button"
                    onClick={handleAddCredits}
                    disabled={addingCredits || !creditsAmount || isNaN(parseInt(creditsAmount, 10))}
                    className="whitespace-nowrap"
                  >
                    {addingCredits ? 'Añadiendo...' : 'Añadir Créditos'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={ADMIN_STATS_ROW_CLASSNAME}>
          <div className={`${ADMIN_STAT_CARD_CLASSNAME} basis-[140px]`}>
            <div className={ADMIN_STAT_LABEL_CLASSNAME}>Proyectos</div>
            <div
              className={cn(
                ADMIN_STAT_VALUE_CLASSNAME,
                data.user.maxProjects !== null && data.stats.projectCount > data.user.maxProjects
                  && 'text-red-600 dark:text-red-400',
              )}
            >
              {data.stats.projectCount}
              {data.user.maxProjects !== null && ` / ${data.user.maxProjects}`}
            </div>
          </div>
          <div className={`${ADMIN_STAT_CARD_CLASSNAME} basis-[140px]`}>
            <div className={ADMIN_STAT_LABEL_CLASSNAME}>Sugerencias Totales</div>
            <div className={ADMIN_STAT_VALUE_CLASSNAME}>{data.stats.suggestionsStats.total}</div>
          </div>
          <div className={`${ADMIN_STAT_CARD_CLASSNAME} basis-[140px]`}>
            <div className={ADMIN_STAT_LABEL_CLASSNAME}>Aplicadas</div>
            <div className="text-2xl font-semibold text-emerald-600 dark:text-emerald-400">{data.stats.suggestionsStats.applied}</div>
          </div>
          <div className={`${ADMIN_STAT_CARD_CLASSNAME} basis-[140px]`}>
            <div className={ADMIN_STAT_LABEL_CLASSNAME}>Tasa de Aplicación</div>
            <div className={ADMIN_STAT_VALUE_CLASSNAME}>{appliedRate}%</div>
          </div>
        </div>

        <div className="mb-8 min-w-0">
          <h2 className={ADMIN_SECTION_TITLE_CLASSNAME}>
            Proyectos ({data.stats.projects.length})
          </h2>
          {data.stats.projects.length === 0 ? (
            <div className={ADMIN_EMPTY_BOX_CLASSNAME}>
              <p>No hay proyectos activos</p>
            </div>
          ) : (
            <AdminTableWrap>
<table className={cn(ADMIN_TABLE_CLASSNAME, "min-w-[600px]")}>
                <thead>
                  <tr>
                    <th>
                      Título
                    </th>
                    <th>
                      Documentos
                    </th>
                    <th>
                      Recursos
                    </th>
                    <th>
                      Detalles
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.stats.projects.map((project) => {
                    const documentsOverLimit = data.user.maxDocumentsPerProject !== null && project.documentCount > data.user.maxDocumentsPerProject;
                    const resourcesOverLimit = data.user.maxResourcesPerProject !== null && project.resourceCount > data.user.maxResourcesPerProject;
                    return (
                      <tr
                        key={project.id}
                      >
                        <td>
                          {project.title}
                        </td>
                        <td className={documentsOverLimit ? 'font-medium text-destructive' : undefined}>
                          {project.documentCount}
                          {data.user.maxDocumentsPerProject !== null && ` / ${data.user.maxDocumentsPerProject}`}
                        </td>
                        <td className={resourcesOverLimit ? 'font-medium text-destructive' : undefined}>
                          {project.resourceCount}
                          {data.user.maxResourcesPerProject !== null && ` / ${data.user.maxResourcesPerProject}`}
                        </td>
                        <td>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => setSelectedProject(project)}
                          >
                            Ver
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </AdminTableWrap>
          )}
        </div>

        <div className="mb-8 min-w-0">
          <h2 className={ADMIN_SECTION_TITLE_CLASSNAME}>
            Documentos ({documentsByLastEdition.length})
          </h2>
          {documentsByLastEdition.length === 0 ? (
            <div className={ADMIN_EMPTY_BOX_CLASSNAME}>
              <p>No hay documentos</p>
            </div>
          ) : (
            <AdminTableWrap>
<table className={cn(ADMIN_TABLE_CLASSNAME, "min-w-[600px]")}>
                <thead>
                  <tr>
                    <th>
                      Documento
                    </th>
                    <th>
                      Proyecto
                    </th>
                    <th>
                      Última Edición
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {documentsByLastEdition.map((doc) => (
                    <tr
                      key={doc.id}
                    >
                      <td>
                        <Link
                          to={`/documents/${doc.id}`}
                          className={ADMIN_TABLE_LINK_CLASSNAME}
                        >
                          {doc.title}
                        </Link>
                      </td>
                      <td className={ADMIN_TABLE_CELL_MUTED_CLASSNAME}>
                        {doc.projectId ? (projectTitleById.get(doc.projectId) ?? '—') : '—'}
                      </td>
                      <td className={ADMIN_TABLE_CELL_MUTED_CLASSNAME}>
                        {new Date(doc.lastModified).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </AdminTableWrap>
          )}
        </div>

        <div className="mb-8 min-w-0">
          <h2 className={ADMIN_SECTION_TITLE_CLASSNAME}>
            Proyectos en la papelera ({trashedDocuments.length})
          </h2>
          {trashedDocuments.length === 0 ? (
            <div className={ADMIN_EMPTY_BOX_CLASSNAME}>
              <p>No hay proyectos en la papelera</p>
            </div>
          ) : (
            <AdminTableWrap>
<table className={cn(ADMIN_TABLE_CLASSNAME, "min-w-[600px]")}>
                <thead>
                  <tr>
                    <th>
                      Título
                    </th>
                    <th>
                      Última Modificación
                    </th>
                    <th>
                      Detalles
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {trashedDocuments.map((doc) => (
                    <tr
                      key={doc.id}
                    >
                      <td>
                        <Link
                          to={`/documents/${doc.id}`}
                          className={ADMIN_TABLE_LINK_CLASSNAME}
                        >
                          {doc.title}
                        </Link>
                      </td>
                      <td className={ADMIN_TABLE_CELL_MUTED_CLASSNAME}>
                        {new Date(doc.lastModified).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                      <td>
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => setSelectedDocument(doc)}
                        >
                          Ver
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </AdminTableWrap>
          )}
        </div>

        <div className="min-w-0">
          <h2 className={ADMIN_SECTION_TITLE_CLASSNAME}>
            Sugerencias ({data.suggestions.length})
          </h2>
          {data.suggestions.length === 0 ? (
            <div className={ADMIN_EMPTY_BOX_CLASSNAME}>
              <p>No hay sugerencias</p>
            </div>
          ) : (
            <>
              <AdminTableWrap>
<table className={cn(ADMIN_TABLE_CLASSNAME, "min-w-[1000px]")}>
                    <thead>
                      <tr>
                        <th>
                          Texto Original
                        </th>
                        <th>
                          Sugerencia
                        </th>
                        <th>
                          Tipo
                        </th>
                        <th>
                          Estado
                        </th>
                        <th>
                          Mostrada
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.suggestions.slice((suggestionsPage - 1) * suggestionsPageSize, suggestionsPage * suggestionsPageSize).map((s) => (
                      <tr
                        key={s.id}
                        onClick={() => setSelectedSuggestion(s)}
                      >
                        <td className="max-w-[200px]">
                          <div className="overflow-hidden text-ellipsis whitespace-nowrap" title={s.originalText}>
                            {s.originalText}
                          </div>
                        </td>
                        <td className="max-w-[200px]">
                          <div className="overflow-hidden text-ellipsis whitespace-nowrap" title={s.suggestionText}>
                            {s.suggestionText}
                          </div>
                        </td>
                        <td>
                          <span
                            className={
                              s.issueType === 'strength'
                                ? ADMIN_BADGE_SUCCESS_CLASSNAME
                                : s.issueType === 'orthographic'
                                  ? ADMIN_BADGE_DANGER_CLASSNAME
                                  : ADMIN_BADGE_WARNING_CLASSNAME
                            }
                          >
                            {getIssueTypeLabel(s.issueType)}
                          </span>
                        </td>
                        <td>
                          {s.status === 'applied' ? (
<span className={ADMIN_BADGE_SUCCESS_CLASSNAME}>Aprobada</span>
                          ) : s.status === 'rejected' ? (
<span className={ADMIN_BADGE_DANGER_CLASSNAME}>Rechazada</span>
                          ) : s.status === 'reported' ? (
<span className={ADMIN_BADGE_WARNING_CLASSNAME}>Reportada</span>
                          ) : (
<span className={ADMIN_BADGE_NEUTRAL_CLASSNAME}>Pendiente</span>
                          )}
                        </td>
                        <td className={ADMIN_TABLE_CELL_MUTED_CLASSNAME}>
                          {new Date(s.shownAt).toLocaleDateString('es-ES', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                      </tr>
                      ))}
                    </tbody>
                  </table>
                </AdminTableWrap>
              <AdminPaginationBar
                page={suggestionsPage}
                totalPages={Math.ceil(data.suggestions.length / suggestionsPageSize)}
                onPageChange={setSuggestionsPage}
              />
            </>
          )}
        </div>
      </div>

      {selectedDocument && (
        <div
          className={`${ADMIN_MODAL_OVERLAY_CLASSNAME} z-[2000]`}
          onClick={() => setSelectedDocument(null)}
        >
          <div
            className={`${ADMIN_MODAL_CLASSNAME} max-w-[600px]`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-6 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                {hasPrevious && (
                  <Button type="button" size="sm" onClick={() => navigateToDocument('prev')}>
                    ← Anterior
                  </Button>
                )}
                {hasNext && (
                  <Button type="button" size="sm" onClick={() => navigateToDocument('next')}>
                    Siguiente →
                  </Button>
                )}
              </div>
              <h2 className="m-0 text-xl font-semibold text-foreground">
                Detalles del Documento
              </h2>
              <button
                type="button"
                onClick={() => setSelectedDocument(null)}
                className="cursor-pointer border-0 bg-transparent p-1 text-2xl leading-none text-muted-foreground hover:text-foreground"
              >
                ×
              </button>
            </div>

            <div className="flex flex-col gap-4">
              <div>
                <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                  Título
                </h3>
                <p className="m-0 text-sm text-foreground">{selectedDocument.title}</p>
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                  Estado
                </h3>
                <p className="m-0 text-sm text-foreground">
                  {selectedDocument.trashed ? 'Papelera' : 'Activo'}
                </p>
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                  Creado
                </h3>
                <p className="m-0 text-sm text-foreground">
                  {new Date(selectedDocument.createdAt).toLocaleDateString('es-ES', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                  Última Modificación
                </h3>
                <p className="m-0 text-sm text-foreground">
                  {new Date(selectedDocument.lastModified).toLocaleDateString('es-ES', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                  Notas de Estilo
                </h3>
                {selectedDocument.styleNotes ? (
                  <div className="max-h-[300px] overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted/40 p-3 text-sm text-foreground">
                    {selectedDocument.styleNotes}
                  </div>
                ) : (
                  <p className="m-0 text-sm italic text-muted-foreground">
                    Sin notas de estilo
                  </p>
                )}
                <StyleNotesCalibrationPanel
                  documentId={selectedDocument.id}
                  onStyleNotesUpdated={(styleNotes) => {
                    setSelectedDocument({
                      ...selectedDocument,
                      styleNotes,
                    });
                    if (data) {
                      setData({
                        ...data,
                        documents: data.documents.map((doc) =>
                          doc.id === selectedDocument.id
                            ? { ...doc, styleNotes }
                            : doc
                        ),
                      });
                    }
                  }}
                />
              </div>

              <div className="mt-4 flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setSelectedDocument(null)}>
                  Cerrar
                </Button>
                <Button asChild>
                  <Link to={`/documents/${selectedDocument.id}`}>
                    Abrir Documento
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedProject && (
        <div
          className={`${ADMIN_MODAL_OVERLAY_CLASSNAME} z-[2000]`}
          onClick={() => setSelectedProject(null)}
        >
          <div
            className={`${ADMIN_MODAL_CLASSNAME} max-w-[800px]`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-6 flex items-center justify-between">
              <h2 className="m-0 text-xl font-semibold text-foreground">
                Detalles del Proyecto
              </h2>
              <button
                type="button"
                onClick={() => setSelectedProject(null)}
                className="cursor-pointer border-0 bg-transparent p-1 text-2xl leading-none text-muted-foreground hover:text-foreground"
              >
                ×
              </button>
            </div>

            <div className="flex flex-col gap-4">
              <div>
                <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                  Título
                </h3>
                <p className="m-0 text-sm text-foreground">{selectedProject.title}</p>
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                  Documentos
                </h3>
                <p className="m-0 text-sm text-foreground">
                  {selectedProject.documentCount}
                  {data?.user.maxDocumentsPerProject !== null && ` / ${data.user.maxDocumentsPerProject}`}
                </p>
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                  Recursos
                </h3>
                <p className="m-0 text-sm text-foreground">
                  {selectedProject.resourceCount}
                  {data?.user.maxResourcesPerProject !== null && ` / ${data.user.maxResourcesPerProject}`}
                </p>
              </div>

              {data && (
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                    Documentos del Proyecto
                  </h3>
                  {data.documents.filter(doc => doc.projectId === selectedProject.id).length === 0 ? (
                    <p className="m-0 text-sm italic text-muted-foreground">
                      No hay documentos en este proyecto
                    </p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {data.documents
                        .filter(doc => doc.projectId === selectedProject.id)
                        .map((doc) => (
                          <div
                            key={doc.id}
                            className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/40 p-3"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="mb-1 text-sm font-medium text-foreground">
                                {doc.title}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {doc.trashed ? 'Papelera' : 'Activo'} • {doc.styleNotes ? 'Con notas de estilo' : 'Sin notas de estilo'}
                              </div>
                            </div>
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => {
                                setSelectedProject(null);
                                setSelectedDocument(doc);
                              }}
                            >
                              Ver Detalles
                            </Button>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}

              <div className="mt-4 flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setSelectedProject(null)}>
                  Cerrar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedSuggestion && (
        <SuggestionDetailAdminModal
          suggestion={selectedSuggestion}
          onClose={() => setSelectedSuggestion(null)}
          onNavigatePrev={() => navigateToSuggestion('prev')}
          onNavigateNext={() => navigateToSuggestion('next')}
          hasPrevious={hasPreviousSuggestion}
          hasNext={hasNextSuggestion}
          showUserEmail={false}
          getIssueTypeLabel={getIssueTypeLabel}
          getStatusLabel={getStatusLabel}
        />
      )}

      <Dialog open={resetStripeCustomerDialogOpen} onOpenChange={setResetStripeCustomerDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin_reset_stripe_customer_id_dialog_title')}</DialogTitle>
            <DialogDescription>{t('admin_reset_stripe_customer_id_dialog_description')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <CancelButton onClick={() => setResetStripeCustomerDialogOpen(false)} disabled={resettingStripeCustomerId} />
            <Button
              variant="destructive"
              onClick={() => void handleResetStripeCustomerId()}
              disabled={resettingStripeCustomerId}
            >
              {resettingStripeCustomerId ? t('loading') : t('admin_reset_stripe_customer_id_confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
